"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslations } from "@/lib/use-translations";

// Dino runner constants (Chrome offline style)
const CANVAS_W = 280;
const CANVAS_H = 160;
const GROUND_Y = CANVAS_H - 20;
const DINO_W = 20;
const DINO_H = 24;
const DINO_X = 40;
const GRAVITY = 0.25;
const JUMP_FORCE = -7;
const OBSTACLE_SPEED_INITIAL = 1.2;
const OBSTACLE_SPEED_MAX = 2.5;
const OBSTACLE_SPAWN_MIN = 425;
const OBSTACLE_SPAWN_MAX = 680;

type ObstacleVariant = "cactus-small" | "cactus-tall" | "rock" | "double-cactus" | "bird";

interface Obstacle {
  x: number;
  w: number;
  h: number;
  scored: boolean;
  variant: ObstacleVariant;
}

export function FlappyBird() {
  const t = useTranslations();
  const g = t.games;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [_score, setScore] = useState(0);
  const [_highScore, setHighScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [started, setStarted] = useState(false);
  // Refs for canvas text (captured by useEffect closure)
  const textRefs = useRef({ tapToStart: g.tapToStart, gameOver: g.gameOver, tapToRestart: g.tapToRestart, hi: g.hi });
  textRefs.current = { tapToStart: g.tapToStart, gameOver: g.gameOver, tapToRestart: g.tapToRestart, hi: g.hi };

  const dinoY = useRef(GROUND_Y);
  const dinoVel = useRef(0);
  const isJumping = useRef(false);
  const obstacles = useRef<Obstacle[]>([]);
  const scoreRef = useRef(0);
  const highScoreRef = useRef(0);
  const frameCount = useRef(0);
  const nextSpawn = useRef(OBSTACLE_SPAWN_MIN);
  const gameOverRef = useRef(false);
  const startedRef = useRef(false);
  const legFrame = useRef(0);

  const jump = useCallback(() => {
    if (gameOverRef.current) {
      // Restart
      dinoY.current = GROUND_Y;
      dinoVel.current = 0;
      isJumping.current = false;
      obstacles.current = [];
      scoreRef.current = 0;
      frameCount.current = 0;
      nextSpawn.current = OBSTACLE_SPAWN_MIN;
      gameOverRef.current = false;
      startedRef.current = true;
      setScore(0);
      setGameOver(false);
      setStarted(true);
      return;
    }
    if (!startedRef.current) {
      startedRef.current = true;
      setStarted(true);
    }
    if (!isJumping.current) {
      dinoVel.current = JUMP_FORCE;
      isJumping.current = true;
    }
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.code === "Space" || e.code === "ArrowUp") {
        e.preventDefault();
        jump();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [jump]);

  // Game loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;

    const getSpeed = () => {
      const t = Math.min(scoreRef.current / 300, 1);
      return OBSTACLE_SPEED_INITIAL + t * (OBSTACLE_SPEED_MAX - OBSTACLE_SPEED_INITIAL);
    };

    const gameLoop = () => {
      animId = requestAnimationFrame(gameLoop);

      const styles = getComputedStyle(canvas);
      const fg = styles.getPropertyValue("--fg").trim() || "#e2e8f0";
      const bg = styles.getPropertyValue("--bg").trim() || "#1a1a2e";
      const mutedFg = styles.getPropertyValue("--muted-fg").trim() || "#64748b";

      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

      // Background
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // Ground
      ctx.strokeStyle = fg;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, GROUND_Y);
      ctx.lineTo(CANVAS_W, GROUND_Y);
      ctx.stroke();

      // Ground texture dots
      ctx.fillStyle = mutedFg;
      for (let x = (frameCount.current * 2) % 12; x < CANVAS_W; x += 12) {
        ctx.fillRect(x, GROUND_Y + 4, 1, 1);
        ctx.fillRect(x + 5, GROUND_Y + 8, 1, 1);
      }

      if (startedRef.current && !gameOverRef.current) {
        frameCount.current++;
        const speed = getSpeed();

        // Physics
        dinoVel.current += GRAVITY;
        dinoY.current += dinoVel.current;
        if (dinoY.current >= GROUND_Y) {
          dinoY.current = GROUND_Y;
          dinoVel.current = 0;
          isJumping.current = false;
        }

        // Score every 6 frames
        if (frameCount.current % 6 === 0) {
          scoreRef.current++;
          setScore(scoreRef.current);
        }

        // Spawn obstacles
        nextSpawn.current -= speed;
        if (nextSpawn.current <= 0) {
          const variants: ObstacleVariant[] = ["cactus-small", "cactus-tall", "rock", "double-cactus", "bird"];
          const variant = variants[Math.floor(Math.random() * variants.length)]!;
          let h: number = 10, w: number = 14;
          switch (variant) {
            case "cactus-small": h = 12 + Math.random() * 6; w = 6 + Math.random() * 4; break;
            case "cactus-tall": h = 22 + Math.random() * 8; w = 8 + Math.random() * 4; break;
            case "rock": h = 8 + Math.random() * 6; w = 12 + Math.random() * 8; break;
            case "double-cactus": h = 16 + Math.random() * 6; w = 16 + Math.random() * 6; break;
            case "bird": h = 10; w = 14; break;
          }
          obstacles.current.push({ x: CANVAS_W + 10, w, h, scored: false, variant });
          nextSpawn.current = OBSTACLE_SPAWN_MIN + Math.random() * (OBSTACLE_SPAWN_MAX - OBSTACLE_SPAWN_MIN);
        }

        // Update obstacles
        obstacles.current = obstacles.current.filter((o) => o.x + o.w > -20);
        for (const obs of obstacles.current) {
          obs.x -= speed;

          // Collision (very generous hitbox — casual game)
          const dinoLeft = DINO_X - DINO_W / 2 + 6;
          const dinoRight = DINO_X + DINO_W / 2 - 6;
          const dinoBot = dinoY.current;

          const obsLeft = obs.x;
          const obsRight = obs.x + obs.w;
          const obsTop = GROUND_Y - obs.h;

          if (dinoRight > obsLeft && dinoLeft < obsRight && dinoBot > obsTop) {
            gameOverRef.current = true;
            if (scoreRef.current > highScoreRef.current) {
              highScoreRef.current = scoreRef.current;
              setHighScore(highScoreRef.current);
            }
            setGameOver(true);
          }
        }
      }

      // Draw obstacles (varied styles)
      ctx.fillStyle = fg;
      for (const obs of obstacles.current) {
        const ox = Math.round(obs.x);
        const oy = GROUND_Y - obs.h;
        switch (obs.variant) {
          case "cactus-small":
            ctx.fillRect(ox + obs.w / 2 - 2, oy, 4, obs.h);
            ctx.fillRect(ox, oy, obs.w, 3);
            break;
          case "cactus-tall":
            ctx.fillRect(ox + obs.w / 2 - 3, oy, 6, obs.h);
            ctx.fillRect(ox, oy, obs.w, 4);
            ctx.fillRect(ox - 2, oy + 8, 4, 10);
            ctx.fillRect(ox + obs.w - 2, oy + 12, 4, 8);
            break;
          case "rock":
            // Rounded-ish rock shape
            ctx.fillRect(ox + 2, oy, obs.w - 4, obs.h);
            ctx.fillRect(ox, oy + 2, obs.w, obs.h - 4);
            ctx.fillRect(ox + 1, oy + 1, obs.w - 2, obs.h - 2);
            break;
          case "double-cactus":
            // Two cacti side by side
            const half = Math.floor(obs.w / 2) - 1;
            ctx.fillRect(ox + half / 2 - 2, oy, 4, obs.h);
            ctx.fillRect(ox, oy, half, 3);
            ctx.fillRect(ox + half + 2 + half / 2 - 2, oy + 3, 4, obs.h - 3);
            ctx.fillRect(ox + half + 2, oy + 3, half, 3);
            break;
          case "bird":
            // Flying bird (sits higher)
            const by = oy - 12;
            ctx.fillRect(ox + 4, by + 4, 6, 4); // body
            ctx.fillRect(ox, by + 2, 4, 2); // left wing
            ctx.fillRect(ox + 10, by, 4, 2); // right wing up
            ctx.fillRect(ox + 2, by + 6, 10, 2); // tail
            break;
        }
      }

      // Draw dino (pixel-art style T-Rex)
      const dx = DINO_X - DINO_W / 2;
      const dy = Math.round(dinoY.current) - DINO_H;
      ctx.fillStyle = fg;

      // Body
      ctx.fillRect(dx + 4, dy + 4, 12, 12);
      // Head
      ctx.fillRect(dx + 10, dy, 10, 10);
      // Eye (cutout)
      ctx.fillStyle = bg;
      ctx.fillRect(dx + 16, dy + 2, 2, 2);
      ctx.fillStyle = fg;
      // Jaw
      ctx.fillRect(dx + 14, dy + 8, 6, 2);
      // Tail
      ctx.fillRect(dx, dy + 6, 6, 4);
      ctx.fillRect(dx - 2, dy + 4, 4, 4);
      // Arms
      ctx.fillRect(dx + 12, dy + 14, 4, 4);

      // Legs (animated when running)
      if (isJumping.current) {
        // Both legs down when jumping
        ctx.fillRect(dx + 6, dy + 16, 3, 8);
        ctx.fillRect(dx + 12, dy + 16, 3, 8);
      } else if (startedRef.current) {
        legFrame.current = (legFrame.current + 1) % 12;
        if (legFrame.current < 6) {
          ctx.fillRect(dx + 6, dy + 16, 3, 8);
          ctx.fillRect(dx + 12, dy + 16, 3, 4);
        } else {
          ctx.fillRect(dx + 6, dy + 16, 3, 4);
          ctx.fillRect(dx + 12, dy + 16, 3, 8);
        }
      } else {
        ctx.fillRect(dx + 6, dy + 16, 3, 8);
        ctx.fillRect(dx + 12, dy + 16, 3, 8);
      }

      // Score
      ctx.fillStyle = fg;
      ctx.font = "bold 12px monospace";
      ctx.textAlign = "right";
      const scoreStr = String(scoreRef.current).padStart(5, "0");
      ctx.fillText(scoreStr, CANVAS_W - 8, 16);

      // High score
      if (highScoreRef.current > 0) {
        ctx.fillStyle = mutedFg;
        ctx.fillText(textRefs.current.hi + " " + String(highScoreRef.current).padStart(5, "0"), CANVAS_W - 70, 16);
      }

      // Messages
      if (!startedRef.current) {
        ctx.fillStyle = fg;
        ctx.font = "12px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(textRefs.current.tapToStart, CANVAS_W / 2, CANVAS_H / 2);
      }

      if (gameOverRef.current) {
        ctx.fillStyle = fg;
        ctx.font = "bold 14px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(textRefs.current.gameOver, CANVAS_W / 2, CANVAS_H / 2 - 12);
        ctx.font = "11px system-ui, sans-serif";
        ctx.fillText(textRefs.current.tapToRestart, CANVAS_W / 2, CANVAS_H / 2 + 6);
      }
    };

    animId = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="text-xs text-muted-foreground/60">
        {started && !gameOver ? g.tapToJump : !started ? g.tapToStart : ""}
      </div>
      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={CANVAS_H}
        onClick={jump}
        onTouchStart={(e) => {
          e.preventDefault();
          jump();
        }}
        className="rounded-xl border-2 border-border cursor-pointer select-none"
        style={{
          // @ts-expect-error -- CSS custom properties
          "--fg": "var(--color-foreground)",
          "--bg": "var(--color-muted)",
          "--muted-fg": "var(--color-muted-foreground)",
        }}
      />
    </div>
  );
}
